import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import useWindowResize from '@picsart/rc/useWindowResize';
import View from '@picsart/view/View';
import { Spacings } from '@picsart/cascade';
import { Button, ButtonSizeLG } from '@picsart/button';
import { useTranslator } from '@picsart/rc/services/localization';
import IconTickLarge from '@picsart/icons/IconTickLarge';

import useMemoSelector from 'hooks/useMemoSelector';
import { getCropAttrs, getCropFlip, getCropRotate } from 'store/batch/selectors';
import { PreviewModalImage, PreviewModalImageBox } from 'pages/Batch/components/PreviewModal/ModalSlices';
import { ICropPatch } from 'types';
import batchSlice from 'store/batch';
import CropImage from 'pages/Batch/components/CropImage';
import usePreviewModal from 'pages/Batch/components/PreviewModal/usePreviewModal';

import useStyles from './styles';

interface ICropModalContentProps {
    imageSrc: string;
    itemKey: string;
    onClose: () => void;
}
const CropModalContent = ({ imageSrc, itemKey, onClose }: ICropModalContentProps) => {
  const translate = useTranslator();
  const dispatch = useDispatch();
  const cropRotate = useMemoSelector(getCropRotate);
  const cropFlip = useMemoSelector(getCropFlip);
  const cropAttrs = useMemoSelector(getCropAttrs);
  const { isImageLoaded, imageRef } = usePreviewModal();
  const [imgSizes, setImgSizes] = useState({ width: 0, height: 0 });
  const [cropSizes, setCropSizes] = useState<ICropPatch>();

  const classes = useStyles({
    cropRotate,
    cropFlip,
  });

  const handleSetImageSize = useCallback(() => {
    if (isImageLoaded) {
      setImgSizes({ width: imageRef.current?.width || 0, height: imageRef.current?.height || 0 });
    }
  }, [imageRef, isImageLoaded]);

  useEffect(() => {
    handleSetImageSize();
  }, [handleSetImageSize]);

  useWindowResize(() => {
    handleSetImageSize();
  });

  const cropAttr = useMemo(() => cropAttrs.find(attr => attr.key === itemKey), [cropAttrs, itemKey]);
  const imageScale = cropAttr && imgSizes.width ? imgSizes.width / cropAttr.initWidth : 1;

  useEffect(() => {
    if (cropAttr && imgSizes.width) {
      setCropSizes({
        x: Math.ceil(cropAttr.x * imageScale),
        y: Math.ceil(cropAttr.y * imageScale),
        width: Math.ceil(cropAttr.width * imageScale),
        height: Math.ceil(cropAttr.height * imageScale),
      });
    }
  }, [cropAttr, imgSizes.width, imageScale]);

  const isCropReady = cropAttr && cropSizes && imgSizes.width && imgSizes.height;

  const onDone = () => {
    if (isCropReady) {
      dispatch(batchSlice.actions.updateCropAttrs({
        ...cropAttr,
        x: Math.ceil(cropSizes.x / imageScale),
        y: Math.ceil(cropSizes.y / imageScale),
        width: Math.ceil(cropSizes.width / imageScale),
        height: Math.ceil(cropSizes.height / imageScale),
        fireUpdate: true,
      }));
    }
    onClose();
  };

  return (
    <>
      <PreviewModalImageBox>
        <PreviewModalImage imageSrc={imageSrc} className={classes.image} />
        {isCropReady && (
          <View position="absolute" inset="0">
            <CropImage
              rotation={cropRotate}
              itemKey={itemKey}
              cropSizes={cropSizes}
              onChange={() => setCropSizes}
              objectWidth={imgSizes.width}
              objectHeight={imgSizes.height}
              isOptionsDraw
            />
          </View>
        )}
      </PreviewModalImageBox>
      <View padding={Spacings.s16} paddingBlockStart={Spacings.s0}>
        <Button onClick={onDone} size={ButtonSizeLG} isFullWidth startIcon={IconTickLarge}>{translate('Done')}</Button>
      </View>
    </>
  );
};

export default CropModalContent;
